import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Mail,
  Loader2,
  ArrowLeft,
  Shield,
  Zap,
  MessageCircle,
  CheckCircle,
  Sparkles,
  Key,
  ArrowRight,
  Sun,
  Star,
  Globe,
  ShieldCheck,
} from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const features = [
    {
      icon: MessageCircle,
      text: "Instant Reset Link",
      gradient: "from-amber-500 to-orange-400",
    },
    {
      icon: Shield,
      text: "Secure Password Recovery",
      gradient: "from-yellow-500 to-orange-300",
    },
    {
      icon: Zap,
      text: "Quick & Easy Process",
      gradient: "from-orange-500 to-amber-400",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email });
      toast.success("smartitbox-powered reset link sent! Check your inbox.");
      setEmailSent(true);
    } catch (error) {
      toast.error(
        "Error sending password reset email: " +
          (error.response?.data?.message || "Unexpected error occurred."),
      );
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const gradientBackground = `
    radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%),
    linear-gradient(to bottom, #0f172a, #1e293b)
  `;

  const floatAnimation = {
    animate: {
      y: [0, -20, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row overflow-hidden bg-gradient-to-br from-amber-50/30 via-white to-orange-50/30">
      {/* Left Side - Brand & Features */}
      <motion.div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Background */}
        <div
          className="absolute inset-0"
          style={{ background: gradientBackground }}
        >
          {/* Sun rays pattern */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 60 + i * 5,
                repeat: Infinity,
                ease: "linear",
              }}
              className="absolute top-1/2 left-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-amber-500/10 to-transparent"
              style={{ transform: `rotate(${i * 45}deg)` }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <motion.div
            className="max-w-lg"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-900/40 to-orange-900/30 rounded-full px-5 py-2.5 mb-8 border border-amber-700/30"
              >
                <span className="text-sm font-semibold text-amber-200">
                  Secure Password Reset
                </span>
                <Star className="w-4 h-4 text-orange-400" />
              </motion.div>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl font-bold text-white mb-8 leading-tight"
              variants={itemVariants}
            >
              <span className="relative">
                <span className="bg-gradient-to-r from-amber-200 via-orange-200 to-amber-200 bg-clip-text text-transparent">
                  Regain Access
                </span>
                <motion.div
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/10 to-yellow-500/20 blur-xl"
                />
              </span>
              <br />
              <span className="relative">
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  Instantly
                </span>
                <motion.span
                  animate={{ width: ["0%", "100%", "0%"] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"
                />
              </span>
            </motion.h1>

            <motion.p
              className="text-xl text-amber-100/80 mb-10 leading-relaxed"
              variants={itemVariants}
            >
              Enter your email address and we'll send you a secure link to reset
              your password in minutes.
            </motion.p>

            {/* Features List */}
            <motion.div className="space-y-6" variants={itemVariants}>
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <motion.div
                    key={index}
                    className="flex items-center gap-5 group"
                    whileHover={{ x: 5, scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="relative">
                      <motion.div
                        animate={{
                          scale: [1, 1.1, 1],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} rounded-xl blur-md opacity-20`}
                      />
                      <div
                        className={`relative w-12 h-12 bg-gradient-to-br ${feature.gradient}/10 backdrop-blur-sm rounded-xl flex items-center justify-center border border-amber-500/30 group-hover:border-amber-400/50 transition-all duration-300`}
                      >
                        <IconComponent className="w-6 h-6 text-amber-400 group-hover:text-amber-300 transition-colors" />
                      </div>
                    </div>
                    <span className="text-white font-medium text-lg">
                      {feature.text}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>

        {/* Central sun decoration */}
        <motion.div
          animate={{
            scale: [1, 1.05, 1],
            rotate: 360,
          }}
          transition={{
            scale: {
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            },
            rotate: {
              duration: 30,
              repeat: Infinity,
              ease: "linear",
            },
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-600/5 to-orange-600/3 rounded-full blur-3xl pointer-events-none"
        />
      </motion.div>

      {/* Right Side - Reset Form */}
      <motion.div
        className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 lg:p-12 relative overflow-y-auto"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      >
        {/* Mobile Logo */}
        <motion.div className="mb-8">
          <div className="relative">
            <img
              src="/logo.png"
              alt="Smart IT Box"
              className="relative h-20 bg-white/90 rounded-xl p-3 border border-amber-500/30 mx-auto"
            />
          </div>
        </motion.div>

        {/* Back button */}
        <motion.button
          onClick={() => navigate("/auth")}
          className="absolute top-6 left-6 flex items-center gap-2 text-amber-700 hover:text-orange-700 font-medium lg:hidden"
          whileHover={{ x: -3 }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Sign In</span>
        </motion.button>

        <div className="w-full max-w-md">
          {/* Success State */}
          {emailSent ? (
            <motion.div
              className="bg-gradient-to-br from-white/90 to-amber-50/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-200/50 shadow-xl text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Sun rays effect */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/20 to-transparent" />

              <div className="relative w-20 h-20 mx-auto mb-6">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-400 rounded-full blur-sm opacity-20"
                />
                <div className="relative w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-50 rounded-full flex items-center justify-center border border-green-200">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                smartitbox Link Sent!
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                We've sent a sun-powered reset link to{" "}
                <span className="font-semibold text-amber-600">{email}</span>.
                Please check your inbox and follow the instructions to reset
                your password.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600">
                  <Sparkles className="w-4 h-4" />
                  <span>The smartitbox link will expire in 1 hour</span>
                </div>

                <motion.button
                  onClick={() => navigate("/auth")}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3.5 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Return to smartitbox Dashboard</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </motion.button>

                <p className="text-sm text-amber-600/80 mt-4">
                  Didn't receive the smartitbox email?{" "}
                  <button
                    onClick={() => setEmailSent(false)}
                    className="text-amber-700 hover:text-orange-700 font-medium"
                  >
                    Try sending again
                  </button>
                </p>
              </div>
            </motion.div>
          ) : (
            /* Reset Form */
            <motion.div
              className="bg-gradient-to-br from-white/90 to-amber-50/50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-amber-200/50"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Reset Your smartitbox Password
                </h2>
                <p className="text-gray-600 text-sm">
                  Enter your email to receive a sun-powered reset link
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    Email Address
                  </label>
                  <div
                    className="relative group"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-500 group-hover:text-amber-500 transition-colors" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className="w-full pl-12 pr-4 py-3 border border-amber-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-300 transition-all duration-300 bg-white/50 text-gray-900 placeholder-gray-500 hover:bg-white/80 hover:border-amber-300"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={loading}
                  className="relative w-full bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 text-white py-3.5 rounded-xl hover:from-amber-600 hover:via-orange-600 hover:to-amber-600 transition-all duration-300 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed group overflow-hidden"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Animated background */}
                  <motion.div
                    animate={{
                      x: ["-100%", "200%"],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"
                  />

                  {/* Content */}
                  <div className="relative flex items-center justify-center gap-3">
                    {loading ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        >
                          <Loader2 className="w-5 h-5" />
                        </motion.div>
                        <span>Sending Link...</span>
                      </>
                    ) : (
                      <>
                        <Key className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        <span>Send Reset Link</span>
                      </>
                    )}
                  </div>

                  {/* Sparkle effect */}
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.7, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="absolute -right-2 -top-2"
                  >
                    <Sparkles className="w-4 h-4 text-yellow-300" />
                  </motion.div>
                </motion.button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-amber-100"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-br from-white/90 to-amber-50/50 text-amber-600">
                    Need help?
                  </span>
                </div>
              </div>

              {/* Help Links */}
              <div className="space-y-3 text-center">
                <Link
                  to="/auth"
                  className="text-amber-600 hover:text-orange-600 font-medium transition-colors duration-200 flex items-center justify-center gap-2 group"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to smartitbox Sign In
                </Link>
                <p className="text-sm text-gray-600">
                  Don't have a smartitbox account?{" "}
                  <Link
                    to="/auth?tab=register"
                    className="text-amber-600 hover:text-orange-600 font-medium"
                  >
                    Create one now
                  </Link>
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Floating decorative elements */}
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          {...floatAnimation}
          animate={{
            ...floatAnimation.animate,
            y: floatAnimation.animate.y.map((y) => y * (i % 2 === 0 ? 1 : -1)),
            transition: {
              ...floatAnimation.animate.transition,
              duration: 5 + i * 2,
            },
          }}
          className={`absolute ${i === 1 ? "top-10 right-10" : i === 2 ? "bottom-10 left-10" : "top-1/2 right-20"} w-24 h-24 rounded-full bg-gradient-to-r from-yellow-300/10 to-orange-300/5 blur-2xl pointer-events-none lg:hidden`}
        />
      ))}
    </div>
  );
}
